import { supabase } from '../utils/supabaseClient';
import {
  RemoteServer,
  CreateRemoteServerRequest,
  UpdateRemoteServerRequest,
  RemoteServerListResponse,
  TestConnectionResponse,
} from '../types/remoteServer';

const API_BASE_URL = '/api/integrations';

// Helper to get auth token
const getAuthToken = async (): Promise<string> => {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('No active session');
  }
  return session.access_token;
};

// Create a new remote server
export const createRemoteServer = async (
  data: CreateRemoteServerRequest
): Promise<RemoteServer> => {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE_URL}/remote-servers`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create remote server');
  }

  const result = await response.json();
  return {
    ...result,
    createdAt: new Date(result.createdAt),
    updatedAt: new Date(result.updatedAt),
  };
};

// List all remote servers
export const listRemoteServers = async (): Promise<RemoteServer[]> => {
  try {
    const token = await getAuthToken();

    const response = await fetch(`${API_BASE_URL}/remote-servers`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      // Check if response is JSON before parsing
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to list remote servers');
      }
      throw new Error(`Integrations service unavailable (${response.status})`);
    }

    const result: RemoteServerListResponse = await response.json();
    return result.servers.map((server) => ({
      ...server,
      createdAt: new Date(server.createdAt),
      updatedAt: new Date(server.updatedAt),
    }));
  } catch (error) {
    if (error instanceof SyntaxError) {
      throw new Error('Integrations service is not available. Please ensure the backend server is running.');
    }
    throw error;
  }
};

// Get a single remote server
export const getRemoteServer = async (serverId: string): Promise<RemoteServer> => {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE_URL}/remote-servers/${serverId}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get remote server');
  }

  const result = await response.json();
  return {
    ...result,
    createdAt: new Date(result.createdAt),
    updatedAt: new Date(result.updatedAt),
  };
};

// Update a remote server
export const updateRemoteServer = async (
  serverId: string,
  data: UpdateRemoteServerRequest
): Promise<RemoteServer> => {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE_URL}/remote-servers/${serverId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update remote server');
  }

  const result = await response.json();
  return {
    ...result,
    createdAt: new Date(result.createdAt),
    updatedAt: new Date(result.updatedAt),
  };
};

// Delete a remote server
export const deleteRemoteServer = async (serverId: string): Promise<void> => {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE_URL}/remote-servers/${serverId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete remote server');
  }
};

// Test connection to a remote server
export const testRemoteServerConnection = async (
  serverId: string
): Promise<TestConnectionResponse> => {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE_URL}/remote-servers/${serverId}/test`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to test connection');
  }

  const result = await response.json();
  if (result.details?.connectedAt) {
    result.details.connectedAt = new Date(result.details.connectedAt);
  }
  return result;
};
